// src/pages/home-landing/components/ProgressSection.jsx

import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../supabaseClient'; // 👈 Importamos el cliente de Supabase

const ProgressSection = ({ className = '' }) => {
  // 1. ESTADOS PARA LA ANIMACIÓN Y DATOS DE PROGRESO
  const [isAnimating, setIsAnimating] = useState(false);
  const [progressData, setProgressData] = useState(null); // 👈 Estado para los datos calculados
  const [loading, setLoading] = useState(true); // 👈 Estado de carga
  const [error, setError] = useState(null); // 👈 Estado de error
  const [isExpanded, setIsExpanded] = useState(false); // 👈 Estado para controlar si está expandido

  // Valores por defecto (se usarán si raffleData es null o incompleto)
  const defaultTicketPrice = 50; 
  const defaultTotalTickets = 5000;

  // Función para cargar los datos del sorteo activo y calcular progreso
  const fetchProgressData = async () => {
    try {
      setLoading(true);

      // 1. CONSULTA DIRECTA A LA FUNCIÓN SQL
      const { data, error } = await supabase
        .rpc('get_sorteo_progress')
        .single();

      if (error) throw error;
      if (!data || data.total_boletos === null) {
        setProgressData(null);
        return;
      }

      const soldTickets = Number(data.boletos_vendidos);
      const availableTickets = Number(data.boletos_disponibles);
      const totalTickets = Number(data.total_boletos);
      const totalRecaudado = Number(data.recaudado);

      const soldPercentage = totalTickets > 0 ? (soldTickets / totalTickets) * 100 : 0;

      // Obtener precio por boleto (opcional, si necesitas mostrarlo)
      const { data: raffle } = await supabase
        .from('raffles')
        .select('precio_boleto, fecha_sorteo')
        .eq('estado', 'activo')
        .single();

      const drawDate = raffle?.fecha_sorteo
        ? new Date(raffle.fecha_sorteo).toLocaleDateString('es-VE')
        : 'N/D';

      console.log('Progreso:', { soldTickets, availableTickets, totalTickets, totalRecaudado });

      setProgressData({
        totalTickets,
        soldTickets,
        remainingTickets: availableTickets,
        availableTickets,
        soldPercentage,
        ticketPrice: raffle?.precio_boleto || 50,
        drawDate,
        totalRecaudado,
      });
    } catch (err) {
      console.error('Error al cargar progreso:', err);
      setError(err.message || 'Error desconocido');
    } finally {
      setLoading(false);
    }
  };

  // Efecto para cargar los datos del sorteo activo y calcular progreso al montar el componente
  useEffect(() => {
    fetchProgressData();

    // Suscripción realtime a 'tickets' para updates
    const channel = supabase
      .channel('purchases-changes') // 👈 Puedes cambiar este nombre si lo deseas, pero no es crítico
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tickets', // 👈 CAMBIO: 'purchases' → 'tickets'
        },
        () => {
          fetchProgressData(); // Recarga al cambiar
        }
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, []);

  // EFECTO DE ANIMACIÓN SIMPLE (Para mantener el pulso visual)
  useEffect(() => {
    // Simular un pulso de actualización para el efecto visual de "tiempo real"
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), 800);
    }, 45000); // Cada 45 segundos

    return () => clearInterval(interval);
  }, []);

  // Si hay error, mostramos un mensaje
  if (error) {
    return (
      <div className={`bg-background py-8 sm:py-16 text-center ${className}`}>
        <p className="text-lg sm:text-xl text-error">⚠️ Error al cargar el progreso del sorteo.</p>
        <p className="mt-2 text-xs sm:text-sm text-gray-600">{error}</p>
      </div>
    );
  }

  // Si está cargando, mostramos un placeholder
  if (loading) {
    return (
      <div className={`bg-background py-8 sm:py-16 text-center ${className}`}>
        <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-t-2 border-secondary mx-auto"></div>
        <p className="mt-2 sm:mt-4 text-sm sm:text-lg">Cargando progreso del sorteo...</p>
      </div>
    );
  }

  // Si no hay datos (no hay sorteo activo), mostramos un mensaje
  if (!progressData) {
    return (
      <div className={`bg-background py-8 sm:py-16 text-center ${className}`}>
        <p className="text-lg sm:text-xl text-warning">ℹ️ No hay ningún sorteo activo en este momento.</p>
      </div>
    );
  }

  // ***************************************************************
  // 3. LÓGICA DE ESTILOS Y MENSAJES (Mantenida de tu código original)
  // ***************************************************************
  const getProgressColor = () => {
    if (progressData.soldPercentage >= 90) return 'from-error to-warning';
    if (progressData.soldPercentage >= 70) return 'from-warning to-secondary';
    return 'from-secondary to-success';
  };

  const getStatusMessage = () => {
    if (progressData.soldPercentage >= 95) return '¡Últimos boletos disponibles!';
    if (progressData.soldPercentage >= 80) return 'Stock limitado - ¡Compra ahora!';
    if (progressData.soldPercentage >= 50) return 'Más del 50% vendido';
    return 'Boletos disponibles';
  };

  const getStatusIcon = () => {
    if (progressData.soldPercentage >= 95) return 'AlertTriangle';
    if (progressData.soldPercentage >= 80) return 'Clock';
    return 'TrendingUp';
  };

  const getStatusColor = () => {
    if (progressData.soldPercentage >= 95) return 'text-error';
    if (progressData.soldPercentage >= 80) return 'text-warning';
    return 'text-success';
  };

  return (
    <div className={`bg-background-light dark:bg-background-dark py-8 sm:py-12 md:py-16 ${className}`}>
      <div className="max-w-6xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 space-y-6 sm:space-y-8">
        
        {/* Nueva sección con diseño moderno */}
        <div className="bg-gray-900/50 dark:bg-gray-900/80 rounded-xl shadow-lg p-4 sm:p-6 md:p-8">
          
          {/* Fondo con patrón sutil y efecto de brillo */}
          <div className="subtle-pattern rounded-xl shadow-inner p-4 sm:p-6 md:p-8 w-full colorful-glow-animation">
            
            {/* Header con iconos */}
            <div className="flex flex-col items-center gap-4 sm:gap-6 mb-8 sm:mb-10">
              {/* Iconos encima del título - RESPONSIVE */}
              <div className="flex items-center justify-center gap-4 sm:gap-6 md:gap-8 mb-3 sm:mb-4">
                <Icon name="Award" size={40} className="sm:w-14 sm:h-14 md:w-16 md:h-16 text-amber-500 drop-shadow-lg filter brightness-110" />
                <Icon name="Medal" size={40} className="sm:w-14 sm:h-14 md:w-16 md:h-16 text-yellow-500 drop-shadow-lg filter brightness-110" />
                <Icon name="Ticket" size={40} className="sm:w-14 sm:h-14 md:w-16 md:h-16 text-green-500 drop-shadow-lg filter brightness-110" />
              </div>
              
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-display font-bold text-gray-800 dark:text-gray-200 tracking-wider text-center leading-tight">
                Avance del sorteo
              </h1>
              <div className="w-12 h-0.5 sm:w-16 sm:h-1 bg-gradient-to-r from-amber-400 via-green-500 to-blue-500 rounded-full"></div>
            </div>

            {/* Barra de progreso clickeable con efectos - CORREGIDA */}
            <div 
              className="group relative w-full cursor-pointer hover:shadow-xl transition-all duration-300"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              
              {/* Layout responsive mejorado */}
              <div className="space-y-4 sm:space-y-0">
                
                {/* Móvil: Título arriba de la barra */}
                <div className="sm:hidden text-center">
                  <p className="text-lg font-display font-extrabold text-gray-800 dark:text-gray-200 tracking-wider">
                    {progressData.soldPercentage?.toFixed(1)}% Vendido
                  </p>
                </div>

                {/* Barra de progreso principal - sin interrupciones */}
                <div className="relative h-16 sm:h-20 md:h-24 lg:h-28 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden shadow-inner">
                  
                  {/* Fondo de ondas animado */}
                  <div 
                    className={`absolute top-0 left-0 h-full glow-animation transition-all duration-500 ease-out ${isAnimating ? 'animate-pulse' : ''}`}
                    style={{ width: `${progressData.soldPercentage}%` }}
                  >
                    <div className="h-full w-full wave-bg wave-mask"></div>
                  </div>

                  {/* Texto centrado en la barra (solo en desktop) */}
                  <div className="hidden sm:flex absolute inset-0 items-center justify-center">
                    <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-md rounded-full py-2 px-6 shadow-lg transform transition-transform duration-300 ease-out group-hover:scale-105 flex items-center gap-2">
                      <Icon name="TrendingUp" size={20} className="text-gray-700 dark:text-gray-300" />
                      <p className="text-lg md:text-xl lg:text-2xl font-display font-extrabold text-gray-800 dark:text-gray-200 tracking-wider">
                        {progressData.soldPercentage?.toFixed(1)}% Vendido
                      </p>
                    </div>
                  </div>

                  {/* Flecha indicadora de expandir/colapsar */}
                  <div className="absolute top-1/2 right-3 sm:right-4 md:right-6 transform -translate-y-1/2">
                    <div className={`transform transition-transform duration-300 ${isExpanded ? 'rotate-180' : 'rotate-0'}`}>
                      <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-full p-2 shadow-lg">
                        <Icon name="ChevronDown" size={16} className="sm:w-5 sm:h-5 text-gray-600 dark:text-gray-400" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Estadísticas desplegables abajo de la barra */}
            <div className={`overflow-hidden transition-all duration-500 ease-in-out ${isExpanded ? 'max-h-96 opacity-100 mt-6 sm:mt-8' : 'max-h-0 opacity-0 mt-0'}`}>
              <div className="flex justify-center">
                
                {/* Boletos Vendidos - Centrado y más prominente */}
                <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl p-4 sm:p-6 md:p-8 text-center border border-gray-200/50 dark:border-gray-700/50 shadow-lg max-w-sm sm:max-w-md w-full transform transition-all duration-500 ease-in-out">
                  <div className="flex items-center justify-center space-x-2 sm:space-x-3 mb-3 sm:mb-4">
                    <Icon name="CheckCircle" size={20} className="sm:w-6 sm:h-6 text-green-500" />
                    <span className="text-base sm:text-lg md:text-xl font-display font-bold text-gray-800 dark:text-gray-200">
                      Boletos Vendidos
                    </span>
                  </div>
                  <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-display font-extrabold text-green-600 dark:text-green-400 mb-1 sm:mb-2">
                    {progressData.soldTickets?.toLocaleString()}
                  </div>
                  <div className="text-xs sm:text-sm md:text-base text-gray-600 dark:text-gray-400 font-medium">
                    de {progressData.totalTickets?.toLocaleString()} totales
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Estilos CSS adicionales para las animaciones */}
      <style jsx>{`
        @keyframes wave-animation {
          0% {
            transform: translateX(0) scale(1.05);
          }
          50% {
            transform: translateX(-10px) scale(1);
          }
          100% {
            transform: translateX(0) scale(1.05);
          }
        }

        @keyframes glow-pulse {
          0%, 100% {
            box-shadow: 0 0 12px 4px rgba(251, 191, 36, 0.4);
          }
          50% {
            box-shadow: 0 0 20px 8px rgba(251, 191, 36, 0.6);
          }
        }

        @keyframes colorful-glow {
          0% {
            box-shadow: 0 0 15px 5px rgba(251, 191, 36, 0.6), 0 0 25px 10px rgba(251, 191, 36, 0.3);
          }
          25% {
            box-shadow: 0 0 15px 5px rgba(34, 197, 94, 0.6), 0 0 25px 10px rgba(34, 197, 94, 0.3);
          }
          50% {
            box-shadow: 0 0 15px 5px rgba(59, 130, 246, 0.6), 0 0 25px 10px rgba(59, 130, 246, 0.3);
          }
          75% {
            box-shadow: 0 0 15px 5px rgba(236, 72, 153, 0.6), 0 0 25px 10px rgba(236, 72, 153, 0.3);
          }
          100% {
            box-shadow: 0 0 15px 5px rgba(251, 191, 36, 0.6), 0 0 25px 10px rgba(251, 191, 36, 0.3);
          }
        }

        .wave-bg {
          background: 
            radial-gradient(circle at 10% 20%, rgb(34, 197, 94) 0%, transparent 20%),
            radial-gradient(circle at 70% 80%, rgb(251, 191, 36) 0%, transparent 20%),
            linear-gradient(90deg, #FBBF24, #22C55E);
          background-size: 200% 200%;
        }

        .wave-mask {
          -webkit-mask-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='white'/%3e%3cpath d='M0 50 Q 50 20, 100 50 T 200 50' stroke='black' stroke-width='3' fill='transparent'/%3e%3c/svg%3e");
          -webkit-mask-size: 150px 100%;
          mask-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='white'/%3e%3cpath d='M0 50 Q 50 20, 100 50 T 200 50' stroke='black' stroke-width='3' fill='transparent'/%3e%3c/svg%3e");
          mask-size: 150px 100%;
          animation: wave-animation 4s ease-in-out infinite;
        }

        .glow-animation {
          animation: glow-pulse 2.5s ease-in-out infinite;
        }

        .subtle-pattern {
          background-color: #ffffff;
          background-image:
            radial-gradient(circle at 50% 50%, rgba(204, 204, 204, 0.04) 0%, rgba(204, 204, 204, 0) 50%),
            linear-gradient(to right, rgba(204, 204, 204, 0.02) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(204, 204, 204, 0.02) 1px, transparent 1px);
          background-size: 100px 100px, 20px 20px, 20px 20px;
        }

        .colorful-glow-animation {
          animation: colorful-glow 10s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default ProgressSection;